import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { isAuthenticated, authFetch, tryRestoreSession } from './services/api';
import LandingPage from './pages/LandingPage';
import SharedProjectPage from './pages/SharedProjectPage';
import AuthPage from './components/AuthPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import AppShell from './AppShell';
import AcceptInvitePage from './pages/AcceptInvitePage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';

// Attempt to restore the session from the HttpOnly refresh cookie once on
// startup. Runs a single POST /auth/refresh before any route is rendered so
// the in-memory access token is populated and isAuthenticated() returns true
// for users who are still within their 7-day refresh window.
// The promise is module-level so it runs exactly once regardless of how many
// route components mount simultaneously.
let _sessionRestored = false;
let _sessionRestorePromise = null;

function getSessionRestorePromise() {
  if (_sessionRestored) return Promise.resolve();
  if (!_sessionRestorePromise) {
    _sessionRestorePromise = tryRestoreSession().finally(() => {
      _sessionRestored = true;
    });
  }
  return _sessionRestorePromise;
}

function useSessionRestore() {
  const [ready, setReady] = useState(_sessionRestored);
  useEffect(() => {
    if (_sessionRestored) { setReady(true); return; }
    getSessionRestorePromise().then(() => setReady(true));
  }, []);
  return ready;
}

function LoginRoute() {
  const navigate = useNavigate();
  const ready = useSessionRestore();
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (ready) setAuthed(isAuthenticated());
  }, [ready]);

  if (!ready) return null; // wait for refresh attempt before deciding
  if (authed) return <Navigate to="/app" replace />;

  return (
    <AuthPage
      onAuth={async () => {
        setAuthed(true);
        // Resume pending invite if any
        const pendingInvite = sessionStorage.getItem('pending_invite_token');
        if (pendingInvite) {
          sessionStorage.removeItem('pending_invite_token');
          navigate(`/invite/accept/${pendingInvite}`);
          return;
        }
        try {
          const res = await authFetch('/api/user/me');
          if (res.ok) {
            const data = await res.json();
            if (data.role === 'admin') {
              navigate('/admin/dashboard');
              return;
            }
            if (data.role === 'sales') {
              navigate('/admin/blog');
              return;
            }
          }
        } catch {}
        navigate('/app');
      }}
    />
  );
}

function AppRoute() {
  const ready = useSessionRestore();
  if (!ready) return null; // show nothing while refresh is in flight
  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  return <AppShell />;
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/shared/:token" element={<SharedProjectPage />} />
      <Route path="/invite/accept/:token" element={<AcceptInvitePage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/admin/*" element={<AppRoute />} />
      <Route path="/app/*" element={<AppRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
